import React, { useState, useContext, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../../context/AuthContext';
import LogoImage from '../common/LogoImage';
import './Auth.css';

// Base URL is now set in utils/axiosConfig.js

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  
  const { setIsAuthenticated, setUser } = useContext(AuthContext);
  const navigate = useNavigate();



  const { email, password } = formData;

  const onChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Check if user is already logged in on component mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // Set auth token header
      axios.defaults.headers.common['x-auth-token'] = token;
      
      // Verify token and get user data
      const checkAuth = async () => {
        try {
          const res = await axios.get('/api/auth/user');
          if (res.data) {
            setIsAuthenticated(true);
            setUser(res.data);
            
            // Save user data to localStorage for persistence
            localStorage.setItem('user', JSON.stringify(res.data));
            
            navigate('/');
          } else {
            throw new Error('No user data received');
          }
        } catch (err) {
          console.error('Auth check failed:', err);
          // Token is invalid or expired
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          delete axios.defaults.headers.common['x-auth-token'];
          setIsAuthenticated(false);
          setUser(null);
        }
      };
      
      checkAuth();
    }
  }, [navigate, setIsAuthenticated, setUser]);

  const onSubmit = async e => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      console.log('Attempting login with:', { email });
      console.log('Server URL:', axios.defaults.baseURL);
      
      // Skip health check as it's causing issues and not necessary for login
      // The main login request will handle any connectivity issues
      
      // Proceed with login attempt
      // Use XMLHttpRequest for maximum compatibility
      console.log('Using XMLHttpRequest for login');
      
      const loginPromise = new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        
        // Use full URL for API endpoint
        const baseUrl = axios.defaults.baseURL || 'http://localhost:5000';
        const apiEndpoint = `${baseUrl}/api/auth`;
        console.log('Using API endpoint:', apiEndpoint);
        
        xhr.open('POST', apiEndpoint, true);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader('Accept', 'application/json');
        
        xhr.onload = function() {
          console.log('XHR response received:', xhr.status);
          
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const data = JSON.parse(xhr.responseText);
              resolve({ data, status: xhr.status });
            } catch (e) {
              console.error('Error parsing response:', e);
              reject(new Error('Invalid response format'));
            }
          } else {
            try {
              const errorData = JSON.parse(xhr.responseText);
              console.log('Server error response:', errorData);
              reject(new Error(errorData.msg || `Server error: ${xhr.status}`));
            } catch (e) {
              reject(new Error(`Request failed with status ${xhr.status}`));
            }
          }
        };
        
        xhr.onerror = function() {
          console.error('XHR request failed');
          reject(new Error('Network error'));
        };
        
        xhr.send(JSON.stringify({ email, password }));
      });
      
      const res = await loginPromise;
      
      console.log('Login response received:', res.status);
      
      if (!res.data?.token || !res.data?.user) {
        throw new Error('Invalid response from server');
      }
      
      // Save token and user data to localStorage
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      
      // Set auth token header
      axios.defaults.headers.common['x-auth-token'] = res.data.token;
      
      // Update auth state
      setIsAuthenticated(true);
      setUser(res.data.user);
      
      console.log('Login successful');
      
      // Redirect to dashboard
      navigate('/dashboard');
    } catch (err) {
      console.error('Login error details:', {
        message: err.message,
        code: err.code,
        response: err.response ? {
          status: err.response.status,
          data: err.response.data
        } : 'No response',
        request: err.request ? 'Request was made but no response received' : 'Request setup error'
      });
      
      let errorMessage = 'Login failed. ';
      
      console.log('Login error response:', err.response?.data);
      
      if (err.response?.data?.msg) {
        // Use the server's error message if available
        errorMessage = err.response.data.msg;
      } else if (err.code === 'ECONNABORTED') {
        errorMessage += 'Request timed out. Server might be overloaded.';
      } else if (err.code === 'ERR_NETWORK') {
        errorMessage += 'Network error. Please check your connection and make sure the server is running.';
      } else if (err.response) {
        errorMessage += err.response.data?.msg || `Server error (${err.response.status})`;
      } else if (err.request) {
        errorMessage += 'No response received from server. Please try again later.';
      } else {
        errorMessage += err.message || 'Unknown error occurred.';
      }
      
      setError(errorMessage);
      setLoading(false);
      
      // Clear any existing auth data
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      delete axios.defaults.headers.common['x-auth-token'];
      setIsAuthenticated(false);
      setUser(null);
    }
  };

  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundColor: '#FFFAFA',
      padding: '20px'
    },
    logo: {
      marginBottom: '30px',
      maxWidth: '400px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '100%'
    },
    card: {
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
      padding: '30px',
      width: '100%',
      maxWidth: '450px'
    },
    title: {
      color: '#243444',
      textAlign: 'center',
      marginBottom: '20px',
      fontWeight: 'bold'
    },
    alert: {
      backgroundColor: '#f8d7da',
      color: '#721c24',
      padding: '10px',
      borderRadius: '4px',
      marginBottom: '20px'
    },
    formGroup: {
      marginBottom: '20px'
    },
    label: {
      display: 'block',
      marginBottom: '5px',
      color: '#4C5454',
      fontWeight: 'bold'
    },
    input: {
      width: '100%',
      padding: '10px',
      borderRadius: '4px',
      border: '1px solid #ddd',
      fontSize: '16px'
    },
    button: {
      width: '100%',
      padding: '12px',
      backgroundColor: '#243444',
      color: '#D2BA7B',
      border: 'none',
      borderRadius: '4px',
      fontSize: '16px',
      fontWeight: 'bold',
      cursor: 'pointer',
      transition: 'background-color 0.3s'
    },
    buttonHover: {
      backgroundColor: '#1a2733'
    },
    link: {
      color: '#243444',
      textDecoration: 'none',
      fontWeight: 'bold'
    },
    footer: {
      textAlign: 'center',
      marginTop: '20px',
      color: '#4C5454'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.logo}>
        <Link to="/" style={{ textDecoration: 'none' }}>
          <LogoImage 
            type="transparent" 
            style={{ maxWidth: '100%', height: '150px' }}
          />
          <h2 style={{ color: '#243444', textAlign: 'center', marginTop: '10px' }}>Business Resilience Navigator</h2>
        </Link>
      </div>
      <div style={styles.card}>
        <h1 style={styles.title}>Sign In</h1>
        {error && <div style={styles.alert}>{error}</div>}
        <form onSubmit={onSubmit}>
          <div style={styles.formGroup}>
            <label htmlFor="email" style={styles.label}>Email Address</label>
            <input
              type="email"
              name="email"
              id="email"
              value={email}
              onChange={onChange}
              required
              style={styles.input}
            />
          </div>
          <div style={styles.formGroup}>
            <label htmlFor="password" style={styles.label}>Password</label>
            <input
              type="password"
              name="password"
              id="password"
              value={password}
              onChange={onChange}
              required
              style={styles.input}
            />
            <div style={{ textAlign: 'right', marginTop: '5px' }}>
              <Link to="/forgot-password" style={{ ...styles.link, fontSize: '14px' }}>Forgot Password?</Link>
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            style={styles.button}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
        <p style={styles.footer}>
          Don't have an account? <Link to="/register" style={styles.link}>Sign Up</Link>
        </p>
      </div>
      <div style={{ marginTop: '30px', textAlign: 'center', color: '#4C5454', fontSize: '14px' }}>
        <Link to="/" style={{ color: '#243444', textDecoration: 'none', display: 'block', marginBottom: '10px' }}>← Back to Home</Link>
        © {new Date().getFullYear()} Business Resilience Navigator. All rights reserved.
      </div>
    </div>
  );
};

export default Login;
